import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, Heart, ImagePlus, MoreVertical, Pencil, Sparkles, Trash2, Zap } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useSupabaseSession } from "@/hooks/use-supabase-session";
import { useToast } from "@/hooks/use-toast";
import { sendProgressEvent } from "@/lib/progress";
import {
  addSocialPostComment,
  createSocialPost,
  deleteSocialActivity,
  deleteSocialPost,
  deleteSocialPostComment,
  listRecentSocialActivitiesInDb,
  listSocialFeedPosts,
  toggleLikeSocialPost,
  toggleSaveSocialPost,
  updateSocialPost,
  updateSocialPostComment,
  type SocialActivityItem,
  type SocialActivityKind,
  type SocialFeedPost,
  type SocialMediaItem,
} from "@/lib/supabase/social";
import { resilientFetch } from "@/lib/supabase/client";
import { cn } from "@/lib/utils";

type FeedItem =
  | { id: string; kind: "post"; createdAt: string; post: SocialFeedPost }
  | { id: string; kind: "activity"; createdAt: string; activity: SocialActivityItem };

type MeditationSession = {
  id: string;
  title: string;
  durationMinutes: number;
  coverImageUrl?: string;
  active: boolean;
  tags?: string[];
};

type DietPlanCard = {
  id: string;
  title: string;
  coverImageUrl?: string;
  active: boolean;
};

type WellnessActivityCard = {
  id: string;
  title: string;
  durationMinutes?: number;
  metaLabel?: string;
  metaValue?: string;
  coverImageUrl?: string;
  active: boolean;
  tags?: string[];
};

type ActivityInteractionState = Record<string, { sentEnergy: boolean; sentHealing: boolean }>;

const ACTIVITY_INTERACTIONS_KEY = "panel_activity_interactions_v1";
const ACTIVITY_VIEWED_KEY = "panel_activity_viewed_v1";

const ENERGY_BUTTON_CLASS = "rounded-full border-transparent bg-[var(--panel-primary)] text-white hover:brightness-95 disabled:opacity-100";
const HEALING_BUTTON_CLASS = "rounded-full border-transparent bg-[var(--panel-primary)] text-white hover:brightness-95 disabled:opacity-100";
const PREMIUM_FEED_CARD_CLASS = "overflow-hidden rounded-3xl border border-[#e5d7e8] bg-[#f8f3f7] p-0 shadow-sm";
const PREMIUM_OVERLAY_GRADIENT_CLASS = "pointer-events-none absolute inset-0 bg-gradient-to-r from-[#f7eff4]/75 via-[#f7eff4]/70 to-[#f7eff4]/60";
const PREMIUM_METRIC_CARD_CLASS = "min-w-0 rounded-2xl border border-white/70 bg-white/65 p-2.5 sm:p-3";
const PREMIUM_FOOTER_CLASS = "space-y-3 border-t border-[#e7dce9] bg-white/80 px-4 py-3";
const PREMIUM_ENERGY_BADGE_CLASS = "rounded-full bg-[#f1def5] px-2 py-0.5 font-semibold text-[#79458f]";
const PREMIUM_ENERGY_BAR_CLASS = "h-2 overflow-hidden rounded-full bg-[#f2eaf5]";
const PREMIUM_ENERGY_BAR_FILL_CLASS = "h-full rounded-full bg-[#b879e4]";
const POST_BODY_PREVIEW_LIMIT = 520;
const DEFAULT_COMPLETION_COVER_IMAGE = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 700" preserveAspectRatio="xMidYMid slice">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#f8e8ff"/>
      <stop offset="50%" stop-color="#d8b4fe"/>
      <stop offset="100%" stop-color="#a78bfa"/>
    </linearGradient>
    <radialGradient id="glow1" cx="20%" cy="25%" r="40%">
      <stop offset="0%" stop-color="#ffffff" stop-opacity="0.75"/>
      <stop offset="100%" stop-color="#ffffff" stop-opacity="0"/>
    </radialGradient>
    <radialGradient id="glow2" cx="80%" cy="75%" r="45%">
      <stop offset="0%" stop-color="#fdf4ff" stop-opacity="0.65"/>
      <stop offset="100%" stop-color="#fdf4ff" stop-opacity="0"/>
    </radialGradient>
  </defs>
  <rect width="1200" height="700" fill="url(#bg)"/>
  <circle cx="220" cy="180" r="230" fill="url(#glow1)"/>
  <circle cx="980" cy="540" r="270" fill="url(#glow2)"/>
</svg>
`)}`;

function applyCoverFallbackImage(target: HTMLImageElement) {
  if (!target) return;
  if (target.dataset.coverFallbackApplied === "1") return;
  target.dataset.coverFallbackApplied = "1";
  target.src = DEFAULT_COMPLETION_COVER_IMAGE;
}

function initials(name: string) {
  return (
    String(name ?? "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toLocaleUpperCase("tr-TR") ?? "")
      .join("") || "?"
  );
}

function formatRelative(iso: string) {
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return "az önce";
  const diff = Date.now() - date.getTime();
  const minutes = Math.max(0, Math.floor(diff / 60_000));
  if (minutes < 1) return "az önce";
  if (minutes < 60) return `${minutes} dk önce`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} sa önce`;
  const days = Math.floor(hours / 24);
  return `${days} gün önce`;
}

function readAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error("Görsel okunamadı"));
    reader.readAsDataURL(file);
  });
}

function renderPostMedia(media: SocialMediaItem[]) {
  const firstMedia = media[0];
  if (!firstMedia) return null;

  return (
    <div className="aspect-video w-full bg-slate-100">
      {firstMedia.type === "video" ? (
        <video src={firstMedia.url} controls className="h-full w-full object-cover" />
      ) : (
        <img src={firstMedia.url} alt="Akış medyası" className="h-full w-full object-cover" />
      )}
    </div>
  );
}

function normalizeText(value: string) {
  return String(value ?? "")
    .toLocaleLowerCase("tr-TR")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u")
    .replace(/[^0-9a-z\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function parsePracticeCompletionPost(body: string): { kind: "meditasyon" | "nefes"; title: string } | null {
  const raw = String(body ?? "").trim();
  if (!raw) return null;

  const breathMatch = raw.match(/^Bugünkü\s+nefes\s+seansımı\s+tamamladım:\s*(.+)$/i);
  if (breathMatch?.[1]) return { kind: "nefes", title: breathMatch[1].trim() };

  const meditationMatch = raw.match(/^Bugünkü\s+meditasyonumu\s+tamamladım:\s*(.+)$/i);
  if (meditationMatch?.[1]) return { kind: "meditasyon", title: meditationMatch[1].trim() };

  return null;
}

function parseActivityCompletionPost(body: string): { kind: "aktivite" | "rituel"; title: string } | null {
  const raw = String(body ?? "").trim();
  if (!raw) return null;

  const activityMatch = raw.match(/^Bugünkü\s+aktivitemi\s+tamamladım:\s*(.+)$/i);
  if (activityMatch?.[1]) return { kind: "aktivite", title: activityMatch[1].trim() };

  const ritualMatch = raw.match(/^Bugünkü\s+ritüelimi\s+tamamladım:\s*(.+)$/i);
  if (ritualMatch?.[1]) return { kind: "rituel", title: ritualMatch[1].trim() };

  return null;
}

function formatSessionDuration(durationMinutes?: number) {
  const minutes = Number(durationMinutes);
  if (!Number.isFinite(minutes) || minutes <= 0) return "—";

  const totalSeconds = Math.max(0, Math.round(minutes * 60));
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;

  if (secs === 0) return `${mins} dk`;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

function parseDurationMinutesFromText(value?: string) {
  const raw = String(value ?? "").trim();
  if (!raw) return undefined;

  const match = raw.match(/(\d+(?:[.,]\d+)?)/);
  if (!match?.[1]) return undefined;

  const parsed = Number(match[1].replace(",", "."));
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return parsed;
}

function resolveCardDurationMinutes(card?: { durationMinutes?: number; metaValue?: string; metaLabel?: string } | null) {
  if (!card) return undefined;

  const direct = Number(card.durationMinutes);
  if (Number.isFinite(direct) && direct > 0) return direct;

  return parseDurationMinutesFromText(card.metaValue) ?? parseDurationMinutesFromText(card.metaLabel);
}

type CompletionVisualCard = {
  title: string;
  coverImageUrl?: string;
  durationMinutes?: number;
  metaValue?: string;
  metaLabel?: string;
};

function formatFocusPercent(value?: number | null) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return "%0";
  return `%${Math.max(0, Math.min(100, Math.round(parsed)))}`;
}

function resolveCompletionVisuals(args: {
  title: string;
  primaryCards: CompletionVisualCard[];
  fallbackCards?: CompletionVisualCard[];
}) {
  const allCards = [...(args.primaryCards ?? []), ...(args.fallbackCards ?? [])];
  const normalizedTitle = normalizeText(args.title);

  const exactMatch = allCards.find((card) => normalizeText(card.title) === normalizedTitle) ?? null;
  const partialMatch =
    allCards.find((card) => {
      const cardTitle = normalizeText(card.title);
      return cardTitle.length >= 3 && normalizedTitle.length >= 3 && (normalizedTitle.includes(cardTitle) || cardTitle.includes(normalizedTitle));
    }) ?? null;

  const matchedCard = exactMatch ?? partialMatch;
  const durationCard = matchedCard ?? allCards.find((card) => resolveCardDurationMinutes(card) !== undefined) ?? null;
  const coverCard = (matchedCard?.coverImageUrl ? matchedCard : null) ?? allCards.find((card) => Boolean(card.coverImageUrl)) ?? null;

  return {
    matchedCard,
    durationMinutes: resolveCardDurationMinutes(durationCard),
    coverImageUrl: coverCard?.coverImageUrl || DEFAULT_COMPLETION_COVER_IMAGE,
  };
}

function resolveCompletionStreakDays(args: {
  activities: SocialActivityItem[];
  authorUserId: string;
  title: string;
  kind: "meditasyon" | "nefes" | "aktivite" | "rituel";
  createdAt: string;
}) {
  const { activities, authorUserId, title, kind, createdAt } = args;
  const normalizedTitle = normalizeText(title);
  const targetTime = new Date(createdAt).getTime();

  const kindMatches = (taskKind: SocialActivityKind) => {
    if (kind === "rituel") return taskKind === "rituel";
    if (kind === "nefes") return taskKind === "nefes";
    if (kind === "meditasyon") return taskKind === "meditasyon";
    return taskKind !== "rituel";
  };

  const candidates = activities.filter((activity) => {
    if (activity.authorUserId !== authorUserId) return false;
    if (!kindMatches(activity.taskKind)) return false;

    const activityTitle = normalizeText(activity.title);
    if (!activityTitle || !normalizedTitle) return false;
    return normalizedTitle.includes(activityTitle) || activityTitle.includes(normalizedTitle);
  });

  if (!candidates.length) return 1;

  const best = candidates
    .slice()
    .sort((a, b) => {
      const aDiff = Math.abs(new Date(a.createdAt).getTime() - targetTime);
      const bDiff = Math.abs(new Date(b.createdAt).getTime() - targetTime);
      return aDiff - bDiff;
    })[0];

  return Math.max(1, Math.round(Number(best?.streakDays) || 1));
}

function isNetworkLikeError(error: unknown) {
  const msg = error instanceof Error ? error.message.toLowerCase() : "";
  return msg.includes("failed to fetch") || msg.includes("network") || msg.includes("abort") || msg.includes("timeout");
}

async function fetchActiveItems<T extends { active?: boolean }>(url: string, fallbackErrorMessage: string): Promise<T[]> {
  try {
    const res = await resilientFetch(url);
    const json = (await res.json().catch(() => null)) as { items?: T[]; error?: string } | null;
    if (!res.ok) throw new Error(json?.error || fallbackErrorMessage);
    return Array.isArray(json?.items) ? json.items.filter((item) => item?.active !== false) : [];
  } catch (error) {
    if (isNetworkLikeError(error)) return [];
    throw error;
  }
}

async function fetchMeditations(): Promise<MeditationSession[]> {
  return await fetchActiveItems<MeditationSession>("/api/numeroloji/meditations", "Meditasyonlar alınamadı");
}

async function fetchBreaths(): Promise<MeditationSession[]> {
  return await fetchActiveItems<MeditationSession>("/api/numeroloji/breaths", "Nefes seansları alınamadı");
}

async function fetchDiets(): Promise<DietPlanCard[]> {
  return await fetchActiveItems<DietPlanCard>("/api/numeroloji/diets", "Diyet planları alınamadı");
}

async function fetchWellnessActivities(): Promise<WellnessActivityCard[]> {
  return await fetchActiveItems<WellnessActivityCard>("/api/numeroloji/activities", "Ritüeller alınamadı");
}

function readActivityInteractionsStore(): ActivityInteractionState {
  try {
    const raw = window.localStorage.getItem(ACTIVITY_INTERACTIONS_KEY) ?? "";
    if (!raw) return {};
    const parsed = JSON.parse(raw) as ActivityInteractionState;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function saveActivityInteractionsStore(store: ActivityInteractionState) {
  try {
    window.localStorage.setItem(ACTIVITY_INTERACTIONS_KEY, JSON.stringify(store));
  } catch {
    // ignore
  }
}

function readViewedActivityStore(): Record<string, true> {
  try {
    const raw = window.localStorage.getItem(ACTIVITY_VIEWED_KEY) ?? "";
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, true>;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function saveViewedActivityStore(store: Record<string, true>) {
  try {
    window.localStorage.setItem(ACTIVITY_VIEWED_KEY, JSON.stringify(store));
  } catch {
    // ignore
  }
}

export default function UserPanelAkis() {
  const { user, loading } = useSupabaseSession();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [caption, setCaption] = useState("");
  const [composerType, setComposerType] = useState<"standard" | "affirmation">("standard");
  const [affirmationStyle, setAffirmationStyle] = useState<"violet" | "rose">("violet");
  const [composerMedia, setComposerMedia] = useState<SocialMediaItem[]>([]);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState("");
  const [menuPostId, setMenuPostId] = useState<string | null>(null);
  const [menuActivityId, setMenuActivityId] = useState<string | null>(null);
  const [commentDraftByPostId, setCommentDraftByPostId] = useState<Record<string, string>>({});
  const [commentVisibleCountByPostId, setCommentVisibleCountByPostId] = useState<Record<string, number>>({});
  const [commentEditBodyById, setCommentEditBodyById] = useState<Record<string, string>>({});
  const [expandedPostBodyById, setExpandedPostBodyById] = useState<Record<string, boolean>>({});
  const [activityInteractions, setActivityInteractions] = useState<ActivityInteractionState>(() => (typeof window === "undefined" ? {} : readActivityInteractionsStore()));
  const [viewedActivityIds, setViewedActivityIds] = useState<Record<string, true>>(() => (typeof window === "undefined" ? {} : readViewedActivityStore()));

  const postsQuery = useQuery({
    queryKey: ["social-feed-posts", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: async () => {
      try {
        return await listSocialFeedPosts(120);
      } catch {
        const previous = queryClient.getQueryData<SocialFeedPost[]>(["social-feed-posts", user?.id]);
        return Array.isArray(previous) ? previous : [];
      }
    },
    staleTime: 5_000,
  });

  const activitiesQuery = useQuery({
    queryKey: ["social-feed-activities", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: async () => {
      try {
        return await listRecentSocialActivitiesInDb(30);
      } catch {
        const previous = queryClient.getQueryData<SocialActivityItem[]>(["social-feed-activities", user?.id]);
        return Array.isArray(previous) ? previous : [];
      }
    },
    staleTime: 5_000,
  });

  const meditationsQuery = useQuery({
    queryKey: ["panel-meditations-activity-card"],
    queryFn: fetchMeditations,
    staleTime: 60_000,
  });

  const breathsQuery = useQuery({
    queryKey: ["panel-breaths-activity-card"],
    queryFn: fetchBreaths,
    staleTime: 60_000,
  });

  const dietsQuery = useQuery({
    queryKey: ["panel-diets-activity-card"],
    queryFn: fetchDiets,
    staleTime: 60_000,
  });

  const wellnessActivitiesQuery = useQuery({
    queryKey: ["panel-wellness-activity-card"],
    queryFn: fetchWellnessActivities,
    staleTime: 60_000,
  });

  const createPostMutation = useMutation({
    mutationFn: () =>
      createSocialPost({
        body: caption,
        media: composerMedia.map((item) => ({ url: item.url, type: item.type })),
        postType: composerType,
        affirmationStyle,
      }),
    onSuccess: async (createdPost) => {
      setCaption("");
      setComposerMedia([]);
      toast({ title: "Paylaşım akışa eklendi" });
      void sendProgressEvent({
        eventType: composerType === "affirmation" ? "affirmation_post" : "general_post",
        sourceId: createdPost?.id,
        metadata: {
          postType: composerType,
          mediaCount: Array.isArray(createdPost?.media) ? createdPost.media.length : 0,
        },
      }).catch(() => {
        // non-blocking analytics/progress event
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["social-feed-posts", user?.id] }),
        queryClient.invalidateQueries({ queryKey: ["social-feed-activities", user?.id] }),
        queryClient.invalidateQueries({ queryKey: ["social-hub-posts", user?.id] }),
        queryClient.invalidateQueries({ queryKey: ["progress-summary", user?.id] }),
      ]);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Paylaşım eklenemedi";
      toast({ title: "Hata", description: message, variant: "destructive" });
    },
  });

  const editPostMutation = useMutation({
    mutationFn: (args: { postId: string; body: string }) => updateSocialPost(args),
    onSuccess: async () => {
      setEditingPostId(null);
      setEditingBody("");
      setMenuPostId(null);
      toast({ title: "Paylaşım güncellendi" });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["social-feed-posts", user?.id] }),
        queryClient.invalidateQueries({ queryKey: ["social-hub-posts", user?.id] }),
      ]);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Paylaşım güncellenemedi";
      toast({ title: "Hata", description: message, variant: "destructive" });
    },
  });

  const deletePostMutation = useMutation({
    mutationFn: (postId: string) => deleteSocialPost(postId),
    onSuccess: async () => {
      setMenuPostId(null);
      toast({ title: "Paylaşım silindi" });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["social-feed-posts", user?.id] }),
        queryClient.invalidateQueries({ queryKey: ["social-hub-posts", user?.id] }),
      ]);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Paylaşım silinemedi";
      toast({ title: "Hata", description: message, variant: "destructive" });
    },
  });

  const deleteActivityMutation = useMutation({
    mutationFn: (activityId: string) => deleteSocialActivity(activityId),
    onSuccess: async () => {
      setMenuActivityId(null);
      toast({ title: "Aktivite paylaşımı silindi" });
      await queryClient.invalidateQueries({ queryKey: ["social-feed-activities", user?.id] });
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Aktivite silinemedi";
      toast({ title: "Hata", description: message, variant: "destructive" });
    },
  });

  const addCommentMutation = useMutation({
    mutationFn: (args: { postId: string; body: string }) => addSocialPostComment(args),
    onSuccess: async (_, variables) => {
      setCommentDraftByPostId((prev) => ({ ...prev, [variables.postId]: "" }));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["social-feed-posts", user?.id] }),
        queryClient.invalidateQueries({ queryKey: ["social-hub-posts", user?.id] }),
      ]);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Yorum gönderilemedi";
      toast({ title: "Hata", description: message, variant: "destructive" });
    },
  });

  const updateCommentMutation = useMutation({
    mutationFn: (args: { postId: string; commentId: string; body: string }) => updateSocialPostComment(args),
    onSuccess: async (_, variables) => {
      setCommentEditBodyById((prev) => {
        const next = { ...prev };
        delete next[variables.commentId];
        return next;
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["social-feed-posts", user?.id] }),
        queryClient.invalidateQueries({ queryKey: ["social-hub-posts", user?.id] }),
      ]);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Yorum güncellenemedi";
      toast({ title: "Hata", description: message, variant: "destructive" });
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: (args: { postId: string; commentId: string }) => deleteSocialPostComment(args),
    onSuccess: async (_, variables) => {
      setCommentEditBodyById((prev) => {
        const next = { ...prev };
        delete next[variables.commentId];
        return next;
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["social-feed-posts", user?.id] }),
        queryClient.invalidateQueries({ queryKey: ["social-hub-posts", user?.id] }),
      ]);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Yorum silinemedi";
      toast({ title: "Hata", description: message, variant: "destructive" });
    },
  });

  const mergedFeed = useMemo<FeedItem[]>(() => {
    const posts = (postsQuery.data ?? []).map<FeedItem>((post) => ({
      id: `post_${post.id}`,
      kind: "post",
      createdAt: post.createdAt,
      post,
    }));

    const activities = (activitiesQuery.data ?? []).map<FeedItem>((activity) => ({
      id: `activity_${activity.id}`,
      kind: "activity",
      createdAt: activity.createdAt,
      activity,
    }));

    return [...posts, ...activities].sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
  }, [activitiesQuery.data, postsQuery.data]);

  const resolveActivityKindMeta = (kind: SocialActivityKind) => {
    if (kind === "nefes") return { completedLabel: "nefes çalışmasını", ctaHref: "/panel/nefes" };
    if (kind === "diyet") return { completedLabel: "diyet planını", ctaHref: "/panel/diyet" };
    if (kind === "rituel") return { completedLabel: "ritüelini", ctaHref: "/panel/rituel" };
    return { completedLabel: "meditasyonunu", ctaHref: "/panel/meditasyon" };
  };

  const resolveMeditationForActivity = (activity: SocialActivityItem) => {
    const meditations = meditationsQuery.data ?? [];
    if (!meditations.length) return null;

    const combined = normalizeText(`${activity.title} ${activity.subtitle}`);
    const matched = meditations.find((item) => {
      const normalizedTitle = normalizeText(item.title);
      return normalizedTitle.length >= 4 && combined.includes(normalizedTitle);
    });

    return matched ?? null;
  };

  const resolveBreathForActivity = (activity: SocialActivityItem) => {
    const breaths = breathsQuery.data ?? [];
    if (!breaths.length) return null;

    const combined = normalizeText(`${activity.title} ${activity.subtitle}`);
    const matched = breaths.find((item) => {
      const normalizedTitle = normalizeText(item.title);
      return normalizedTitle.length >= 4 && combined.includes(normalizedTitle);
    });

    return matched ?? null;
  };

  const resolveDietForActivity = (activity: SocialActivityItem) => {
    const diets = dietsQuery.data ?? [];
    if (!diets.length) return null;
    const combined = normalizeText(`${activity.title} ${activity.subtitle}`);
    const matched = diets.find((item) => {
      const normalizedTitle = normalizeText(item.title);
      return combined.includes(normalizedTitle) || normalizedTitle.includes(combined);
    });
    return matched ?? diets[0];
  };

  const resolveRitualForActivity = (activity: SocialActivityItem) => {
    const rituals = wellnessActivitiesQuery.data ?? [];
    if (!rituals.length) return null;
    const combined = normalizeText(`${activity.title} ${activity.subtitle}`);
    const matched = rituals.find((item) => {
      const normalizedTitle = normalizeText(item.title);
      return combined.includes(normalizedTitle) || normalizedTitle.includes(combined);
    });
    return matched ?? rituals[0];
  };

  const toggleActivityInteraction = (activityId: string, key: "sentEnergy" | "sentHealing") => {
    setActivityInteractions((prev) => {
      const current = prev[activityId] ?? { sentEnergy: false, sentHealing: false };
      const nextForActivity = {
        ...current,
        [key]: !current[key],
      };

      const merged = {
        ...prev,
        [activityId]: nextForActivity,
      };

      saveActivityInteractionsStore(merged);
      return merged;
    });
  };

  const trackCommunityProgressEvent = (eventType: "energy_sent" | "healing_sent", sourceId: string) => {
    void sendProgressEvent({ eventType, sourceId })
      .then(() => queryClient.invalidateQueries({ queryKey: ["progress-summary", user?.id] }))
      .catch(() => {
        // non-blocking analytics/progress event
      });
  };

  const applyOptimisticPostToggle = (postId: string, field: "likedByMe" | "savedByMe") => {
    let nextValue = false;

    queryClient.setQueryData<SocialFeedPost[] | undefined>(["social-feed-posts", user?.id], (prev) => {
      if (!Array.isArray(prev)) return prev;

      return prev.map((item) => {
        if (item.id !== postId) return item;

        const currentValue = Boolean(item[field]);
        nextValue = !currentValue;
        const delta = nextValue ? 1 : -1;

        if (field === "likedByMe") {
          return {
            ...item,
            likedByMe: nextValue,
            likeCount: Math.max(0, item.likeCount + delta),
          } satisfies SocialFeedPost;
        }

        return {
          ...item,
          savedByMe: nextValue,
          saveCount: Math.max(0, item.saveCount + delta),
        } satisfies SocialFeedPost;
      });
    });

    return nextValue;
  };

  const handlePostInteractionClick = async (postId: string, action: "energy" | "healing") => {
    const becameActive = applyOptimisticPostToggle(postId, action === "energy" ? "likedByMe" : "savedByMe");

    if (becameActive) {
      trackCommunityProgressEvent(action === "energy" ? "energy_sent" : "healing_sent", postId);
    }

    try {
      if (action === "energy") {
        await toggleLikeSocialPost(postId);
      } else {
        await toggleSaveSocialPost(postId);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Etkileşim güncellenemedi";
      toast({ title: "Hata", description: message, variant: "destructive" });
    } finally {
      await queryClient.invalidateQueries({ queryKey: ["social-feed-posts", user?.id] });
    }
  };

  const handlePostDoubleClick = async (post: SocialFeedPost, target: EventTarget | null) => {
    const clickedElement = target instanceof HTMLElement ? target : null;
    if (clickedElement?.closest("button, a, textarea, input, video")) return;
    if (editingPostId === post.id) return;

    try {
      if (post.savedByMe) {
        if (!post.likedByMe) {
          await toggleLikeSocialPost(post.id);
          trackCommunityProgressEvent("energy_sent", post.id);
        }
        await toggleSaveSocialPost(post.id);
      } else {
        if (post.likedByMe) await toggleLikeSocialPost(post.id);
        await toggleSaveSocialPost(post.id);
        trackCommunityProgressEvent("healing_sent", post.id);
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["social-feed-posts", user?.id] }),
        queryClient.invalidateQueries({ queryKey: ["progress-summary", user?.id] }),
      ]);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Etkileşim güncellenemedi";
      toast({ title: "Hata", description: message, variant: "destructive" });
    }
  };

  const submitCommentForPost = (postId: string) => {
    const body = String(commentDraftByPostId[postId] ?? "").trim();
    if (!body) return;
    void addCommentMutation.mutate({ postId, body });
  };

  const startEditComment = (commentId: string, body: string) => {
    setCommentEditBodyById((prev) => ({ ...prev, [commentId]: body }));
  };

  const cancelEditComment = (commentId: string) => {
    setCommentEditBodyById((prev) => {
      const next = { ...prev };
      delete next[commentId];
      return next;
    });
  };

  const saveEditedComment = (postId: string, commentId: string) => {
    const body = String(commentEditBodyById[commentId] ?? "").trim();
    if (!body) return;
    void updateCommentMutation.mutate({ postId, commentId, body });
  };

  const showMoreComments = (postId: string) => {
    setCommentVisibleCountByPostId((prev) => ({
      ...prev,
      [postId]: (prev[postId] ?? 10) + 10,
    }));
  };

  const renderExpandablePostBody = (args: {
    postId: string;
    body: string;
    textClassName: string;
    buttonClassName: string;
    quoted?: boolean;
  }) => {
    const { postId, body, textClassName, buttonClassName, quoted = false } = args;
    const safeBody = String(body ?? "").trim();
    const shouldTruncate = safeBody.length > POST_BODY_PREVIEW_LIMIT;
    const isExpanded = Boolean(expandedPostBodyById[postId]);
    const visibleBody = shouldTruncate && !isExpanded ? `${safeBody.slice(0, POST_BODY_PREVIEW_LIMIT).trimEnd()}...` : safeBody;

    if (!safeBody) return null;

    return (
      <div className="space-y-1">
        <p className={textClassName}>{quoted ? `“${visibleBody}”` : visibleBody}</p>
        {shouldTruncate ? (
          <button
            type="button"
            className={cn("text-xs font-semibold underline underline-offset-2", buttonClassName)}
            onClick={() =>
              setExpandedPostBodyById((prev) => ({
                ...prev,
                [postId]: !prev[postId],
              }))
            }
          >
            {isExpanded ? "Daha az göster" : "Devamını oku"}
          </button>
        ) : null}
      </div>
    );
  };

  const renderPostComments = (post: SocialFeedPost, variant: "default" | "dark" = "default") => {
    const draft = commentDraftByPostId[post.id] ?? "";
    const comments = post.comments ?? [];
    const visibleCount = commentVisibleCountByPostId[post.id] ?? 10;
    const visibleComments = comments.slice(0, visibleCount);
    const hasMore = comments.length > visibleCount;
    const isPendingForPost = addCommentMutation.isPending && addCommentMutation.variables?.postId === post.id;

    return (
      <div className="space-y-2">
        <div className={cn("text-xs", variant === "dark" ? "text-white/85" : "text-slate-500")}>{post.commentCount} yorum</div>

        <div className="flex items-start gap-2">
          <Textarea
            value={draft}
            onChange={(event) => setCommentDraftByPostId((prev) => ({ ...prev, [post.id]: event.target.value }))}
            maxLength={300}
            placeholder="Yorum yaz..."
            className={cn(
              "min-h-[40px] resize-none text-sm",
              variant === "dark"
                ? "border-white/30 bg-white/20 text-white placeholder:text-white/70"
                : "border-[#ddc6df] bg-white text-slate-700 placeholder:text-slate-400",
            )}
          />
          <Button
            type="button"
            size="sm"
            className="mt-1"
            disabled={isPendingForPost || !draft.trim()}
            onClick={() => submitCommentForPost(post.id)}
          >
            Gönder
          </Button>
        </div>

        {visibleComments.length ? (
          <div className="space-y-1">
            {visibleComments.map((comment) => {
              const canManage = comment.userId === user?.id;
              const editingValue = commentEditBodyById[comment.id];
              const isEditing = typeof editingValue === "string";
              const isUpdating =
                updateCommentMutation.isPending &&
                updateCommentMutation.variables?.postId === post.id &&
                updateCommentMutation.variables?.commentId === comment.id;
              const isDeleting =
                deleteCommentMutation.isPending &&
                deleteCommentMutation.variables?.postId === post.id &&
                deleteCommentMutation.variables?.commentId === comment.id;

              return (
                <div
                  key={comment.id}
                  className={cn(
                    "rounded-lg px-2 py-1.5 text-xs",
                    variant === "dark" ? "bg-white/20 text-white" : "bg-[#f4edf6] text-slate-700",
                  )}
                >
                  {isEditing ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editingValue}
                        onChange={(event) => setCommentEditBodyById((prev) => ({ ...prev, [comment.id]: event.target.value }))}
                        maxLength={300}
                        className={cn(
                          "min-h-[44px] resize-none text-xs",
                          variant === "dark"
                            ? "border-white/30 bg-white/20 text-white placeholder:text-white/70"
                            : "border-[#ddc6df] bg-white text-slate-700 placeholder:text-slate-400",
                        )}
                      />
                      <div className="flex items-center justify-end gap-1">
                        <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => cancelEditComment(comment.id)}>
                          Vazgeç
                        </Button>
                        <Button type="button" size="sm" className="h-7 px-2 text-[11px]" disabled={isUpdating || !editingValue.trim()} onClick={() => saveEditedComment(post.id, comment.id)}>
                          Kaydet
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className="font-semibold">{comment.userName}: </span>
                        <span>{comment.body}</span>
                      </div>
                      {canManage ? (
                        <div className="flex items-center gap-1">
                          <Button type="button" size="icon" variant="ghost" className={cn("h-6 w-6", variant === "dark" && "text-white hover:bg-white/20")} onClick={() => startEditComment(comment.id, comment.body)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button type="button" size="icon" variant="ghost" className={cn("h-6 w-6 text-red-500 hover:bg-red-50", variant === "dark" && "hover:bg-red-500/20")} disabled={isDeleting} onClick={() => void deleteCommentMutation.mutate({ postId: post.id, commentId: comment.id })}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : null}

        {hasMore ? (
          <button
            type="button"
            onClick={() => showMoreComments(post.id)}
            className={cn("text-xs font-semibold underline underline-offset-2", variant === "dark" ? "text-white/90" : "text-[#7a4a95]")}
          >
            Devamı oku
          </button>
        ) : null}
      </div>
    );
  };

  useEffect(() => {
    const activities = (activitiesQuery.data ?? []).map((it) => it.id);
    if (!activities.length) return;

    const newIds = activities.filter((id) => !viewedActivityIds[id]);
    if (!newIds.length) return;

    setViewedActivityIds((prev) => {
      const next = { ...prev };
      for (const id of newIds) next[id] = true;
      saveViewedActivityStore(next);
      return next;
    });

  }, [activitiesQuery.data, viewedActivityIds]);

  const onPickImageFiles = async (files: FileList | null) => {
    if (!files?.length) return;

    const picked = Array.from(files).slice(0, Math.max(0, 4 - composerMedia.length));
    if (!picked.length) return;

    try {
      const urls = await Promise.all(picked.map((file) => readAsDataUrl(file)));
      setComposerMedia((prev) => [
        ...prev,
        ...urls.map<SocialMediaItem>((url, index) => ({
          id: `akis_file_${Date.now()}_${index}`,
          type: picked[index]?.type.startsWith("video") ? "video" : "image",
          url,
        })),
      ]);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Görsel eklenemedi";
      toast({ title: "Hata", description: message, variant: "destructive" });
    }
  };

  if (!user?.id && !loading) {
    return <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600">Akış için giriş yapmalısın.</div>;
  }

  return (
    <div className="mx-auto w-full max-w-4xl space-y-5 pt-2 pb-24 sm:space-y-6 sm:pt-3 sm:pb-0">
      <div className="space-y-1">
        <h1 className="text-2xl font-black tracking-tight text-slate-900 dark:text-slate-100 sm:text-3xl">Topluluk Akışı</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">Paylaşımları ve tamamlanan çalışmalarını burada görebilirsin.</p>
      </div>

      <Card className="rounded-2xl border border-[#e6d5e8] bg-white p-4 shadow-sm dark:bg-slate-900/40">
        <Textarea
          value={caption}
          onChange={(event) => setCaption(event.target.value)}
          placeholder={composerType === "affirmation" ? "Olumlama metnini yaz..." : "Bugünkü niyetini veya çalışmanı paylaş..."}
          maxLength={3000}
          className="min-h-[92px] resize-none rounded-xl border-[#ddc6df] bg-[#f9f3fa] text-[#3d2f47] placeholder:text-[#8a7893]"
        />

        {composerMedia.length ? (
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {composerMedia.map((media) => (
              <div key={media.id} className="relative overflow-hidden rounded-lg border border-slate-200">
                <div className="aspect-square bg-slate-100">
                  {media.type === "video" ? <video src={media.url} className="h-full w-full object-cover" /> : <img src={media.url} alt="Yüklenen medya" className="h-full w-full object-cover" />}
                </div>
                <button
                  type="button"
                  className="absolute right-1 top-1 rounded-full bg-black/60 px-1.5 py-0.5 text-xs text-white"
                  onClick={() => setComposerMedia((prev) => prev.filter((item) => item.id !== media.id))}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        ) : null}

        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant={composerType === "affirmation" ? "default" : "outline"}
              className={cn("rounded-xl", composerType === "affirmation" && "bg-[#8e6cf2] hover:bg-[#7b58e8]")}
              onClick={() => setComposerType((prev) => (prev === "affirmation" ? "standard" : "affirmation"))}
            >
              <Sparkles className="mr-1 h-4 w-4" /> Olumlama
            </Button>

            {composerType === "affirmation" ? (
              <Button
                type="button"
                variant="outline"
                className="rounded-xl border-[#dfc4e2] bg-[#f8f0fb] text-[#5e3d71]"
                onClick={() => setAffirmationStyle((prev) => (prev === "violet" ? "rose" : "violet"))}
              >
                Stil: {affirmationStyle === "violet" ? "Mor" : "Pembe"}
              </Button>
            ) : null}

            <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-[#dfc4e2] bg-[#f8f0fb] px-3 py-2 text-sm text-[#5e3d71]">
              <ImagePlus className="h-4 w-4" /> Görsel Ekle
              <input type="file" accept="image/*" multiple className="hidden" onChange={(event) => void onPickImageFiles(event.currentTarget.files)} />
            </label>
          </div>

          <div className="flex items-center gap-2">
            <div className="text-xs text-slate-500">{caption.length}/3000</div>
            <Button
              type="button"
              className="rounded-xl bg-[#ed6a24] px-6 text-white hover:bg-[#db5c19]"
              disabled={createPostMutation.isPending || (!caption.trim() && !composerMedia.length)}
              onClick={() => createPostMutation.mutate()}
            >
              Paylaş
            </Button>
          </div>
        </div>
      </Card>

      <div className="space-y-4">
        {mergedFeed.length ? (
          mergedFeed.map((item) => {
            if (item.kind === "activity") {
              const activity = item.activity;
              const activityMeta = resolveActivityKindMeta(activity.taskKind);
              const meditation = activity.taskKind === "meditasyon" ? resolveMeditationForActivity(activity) : null;
              const breath = activity.taskKind === "nefes" ? resolveBreathForActivity(activity) : null;
              const dietPlan = activity.taskKind === "diyet" ? resolveDietForActivity(activity) : null;
              const ritual = activity.taskKind === "rituel" ? resolveRitualForActivity(activity) : null;
              const activityDisplayTitle = dietPlan?.title ?? ritual?.title ?? breath?.title ?? meditation?.title ?? activity.title;
              const activityDuration = breath?.durationMinutes ?? meditation?.durationMinutes ?? ritual?.durationMinutes ?? 25;
              const interaction = activityInteractions[activity.id] ?? { sentEnergy: false, sentHealing: false };
              const viewCount = Math.max(1, viewedActivityIds[activity.id] ? 1 : 0);
              const energyCount = interaction.sentEnergy ? 1 : 0;
              const healingCount = interaction.sentHealing ? 1 : 0;
              const interactionCount = interaction.sentEnergy || interaction.sentHealing ? 1 : 0;
              const collectiveEnergyPercent = Math.max(0, Math.min(100, Math.round((interactionCount / viewCount) * 100)));
              const isOwnActivity = activity.authorUserId === user?.id;
              const activityAuthorName = activity.authorName || "Kullanıcı";
              const activityAuthorHandle = activity.authorHandle ? `@${activity.authorHandle}` : "@kullanici";
              const activityAuthorPhotoUrl = activity.authorPhotoUrl ?? "";
              const activityProfileHref = isOwnActivity ? "/panel/profil" : `/panel/kesfet/profil/${activity.authorUserId}`;
              const activityBackgroundImageUrl = dietPlan?.coverImageUrl ?? ritual?.coverImageUrl ?? breath?.coverImageUrl ?? meditation?.coverImageUrl ?? DEFAULT_COMPLETION_COVER_IMAGE;

              return (
                <Card key={item.id} className={PREMIUM_FEED_CARD_CLASS}>
                  <div className="relative px-3 pb-4 pt-4 sm:px-6 sm:pb-5 sm:pt-6">
                    {activityBackgroundImageUrl ? (
                      <>
                        <div className="pointer-events-none absolute inset-0">
                          <img src={activityBackgroundImageUrl} alt={activityDisplayTitle} className="h-full w-full object-cover opacity-50" onError={(event) => applyCoverFallbackImage(event.currentTarget)} />
                        </div>
                        <div className={PREMIUM_OVERLAY_GRADIENT_CLASS} />
                      </>
                    ) : null}

                    <div className="relative z-10 mb-2 flex justify-end">
                      {isOwnActivity ? (
                        <div className="relative">
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            className="h-8 w-8 rounded-full border-white/90 bg-white text-slate-700 shadow-sm hover:bg-slate-100"
                            onClick={() => setMenuActivityId((prev) => (prev === activity.id ? null : activity.id))}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>

                          {menuActivityId === activity.id ? (
                            <div className="absolute right-0 z-20 mt-2 w-36 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg">
                              <button
                                type="button"
                                disabled={deleteActivityMutation.isPending}
                                className="flex w-full items-center rounded-lg px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-60"
                                onClick={() => {
                                  void deleteActivityMutation.mutate(activity.id);
                                  setMenuActivityId(null);
                                }}
                              >
                                <Trash2 className="mr-2 h-4 w-4" /> Sil
                              </button>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>

                    <div className="relative mx-auto max-w-2xl text-center">
                      <h3 className="break-words text-xl font-black leading-tight tracking-tight text-[#0f172a] sm:text-3xl">
                        {activityAuthorName}, {activityDisplayTitle} {activityMeta.completedLabel} tamamladı!
                      </h3>
                      <p className="mt-1 text-sm text-slate-600 sm:text-lg">{activity.subtitle}</p>

                      <div className="mt-4 grid grid-cols-3 gap-2 sm:mt-5 sm:gap-3">
                        <div className={PREMIUM_METRIC_CARD_CLASS}>
                          <div className="break-words text-[10px] font-bold uppercase leading-tight tracking-[0.08em] text-slate-500">Süre</div>
                          <div className="mt-1 text-2xl font-black leading-none text-[#d96a23] sm:text-3xl">{Math.max(5, Math.round(activityDuration))}dk</div>
                        </div>
                        <div className={PREMIUM_METRIC_CARD_CLASS}>
                          <div className="break-words text-[10px] font-bold uppercase leading-tight tracking-[0.08em] text-slate-500">Kolektif Enerji</div>
                          <div className="mt-1 text-2xl font-black leading-none text-[#d96a23] sm:text-3xl">{formatFocusPercent(collectiveEnergyPercent)}</div>
                        </div>
                        <div className={PREMIUM_METRIC_CARD_CLASS}>
                          <div className="break-words text-[10px] font-bold uppercase leading-tight tracking-[0.08em] text-slate-500">Seri</div>
                          <div className="mt-1 text-2xl font-black leading-none text-[#d96a23] sm:text-3xl">{activity.streakDays} G.</div>
                        </div>
                      </div>

                      <div className="mt-5 flex justify-center">
                        <Button asChild type="button" className="rounded-xl bg-white text-slate-900 hover:bg-white/90">
                          <Link to={activityMeta.ctaHref}>Sen de Tamamla</Link>
                        </Button>
                      </div>

                      <div className="mt-4 flex flex-col items-center justify-center gap-2">
                        <Link to={activityProfileHref} className="rounded-full">
                          <Avatar className="h-12 w-12 border border-white/70 shadow-sm">
                            <AvatarImage src={activityAuthorPhotoUrl} alt={activityAuthorName} />
                            <AvatarFallback>{initials(activityAuthorName)}</AvatarFallback>
                          </Avatar>
                        </Link>
                        <Link to={activityProfileHref} className="text-base font-semibold text-slate-900 hover:underline">
                          {activityAuthorName}
                        </Link>
                        <div className="break-words px-2 text-center text-[11px] text-slate-500 sm:text-xs">{activityAuthorHandle} • {formatRelative(activity.createdAt)}</div>
                      </div>

                    </div>
                  </div>

                  <div className={PREMIUM_FOOTER_CLASS}>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Eye className="h-3.5 w-3.5" />
                      <span>{viewCount} görüntülenme</span>
                    </div>

                    <div className={PREMIUM_ENERGY_BAR_CLASS}>
                      <div className={PREMIUM_ENERGY_BAR_FILL_CLASS} style={{ width: `${Math.max(2, collectiveEnergyPercent)}%` }} />
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs text-slate-500">{energyCount} enerji • {healingCount} şifa</div>

                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className={ENERGY_BUTTON_CLASS}
                          onClick={() => toggleActivityInteraction(activity.id, "sentEnergy")}
                        >
                          <Zap className={cn("h-3.5 w-3.5", interaction.sentEnergy && "fill-current")} />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className={HEALING_BUTTON_CLASS}
                          onClick={() => toggleActivityInteraction(activity.id, "sentHealing")}
                        >
                          <Heart className={cn("h-3.5 w-3.5", interaction.sentHealing && "fill-current")} />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            }

            const post = item.post;
            const isAffirmation = post.postType === "affirmation";
            const isOwnPost = post.authorUserId === user?.id;
            const isEditing = editingPostId === post.id;
            const practiceCompletion = parsePracticeCompletionPost(post.body);
            const activityCompletion = parseActivityCompletionPost(post.body);

            if (isAffirmation) {
              const isRose = post.affirmationStyle === "rose";
              return (
                <Card
                  key={item.id}
                  onDoubleClick={(event) => void handlePostDoubleClick(post, event.target)}
                  className={cn(
                    "overflow-hidden rounded-[30px] border p-0 shadow-md",
                    isRose
                      ? "border-[#f1d4df] bg-gradient-to-br from-[#ffeef5] via-[#f8e6ff] to-[#f0d8ff]"
                      : "border-[#c8b4ee] bg-gradient-to-br from-[#8f88ee] via-[#9b89f2] to-[#bb6df7]",
                  )}
                >
                  <div className="p-6 text-center sm:p-8">
                    {isOwnPost ? (
                      <div className="mb-2 flex justify-end">
                        <div className="relative">
                          <Button
                            type="button"
                            size="icon"
                            variant="secondary"
                            className="h-8 w-8 rounded-full"
                            onClick={() => setMenuPostId((prev) => (prev === post.id ? null : post.id))}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>

                          {menuPostId === post.id ? (
                            <div className="absolute right-0 z-20 mt-2 w-36 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg">
                              <button
                                type="button"
                                className="flex w-full items-center rounded-lg px-2 py-1.5 text-sm hover:bg-slate-100"
                                onClick={() => {
                                  setEditingPostId(post.id);
                                  setEditingBody(post.body);
                                  setMenuPostId(null);
                                }}
                              >
                                <Pencil className="mr-2 h-4 w-4" /> Düzenle
                              </button>
                              <button
                                type="button"
                                disabled={deletePostMutation.isPending}
                                className="flex w-full items-center rounded-lg px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-60"
                                onClick={() => {
                                  void deletePostMutation.mutate(post.id);
                                  setMenuPostId(null);
                                }}
                              >
                                <Trash2 className="mr-2 h-4 w-4" /> Sil
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                    <div className={cn("text-xs font-bold uppercase tracking-[0.2em]", isRose ? "text-[#af5e7e]" : "text-[#ece3ff]")}>Olumlama Paylaşımı</div>
                    {post.body
                      ? renderExpandablePostBody({
                          postId: post.id,
                          body: post.body,
                          textClassName: cn("mx-auto mt-5 max-w-2xl text-3xl font-medium italic leading-relaxed", isRose ? "text-[#3b2b48]" : "text-white"),
                          buttonClassName: isRose ? "text-[#7a4a95]" : "text-white/90",
                          quoted: true,
                        })
                      : null}

                    <div className="mt-6 flex flex-col items-center justify-center gap-2">
                      <Avatar className="h-16 w-16 border-2 border-white/70">
                        <AvatarImage src={post.authorPhotoUrl ?? ""} alt={post.authorName} />
                        <AvatarFallback>{initials(post.authorName)}</AvatarFallback>
                      </Avatar>
                      <Link
                        to={post.authorUserId === user?.id ? "/panel/profil" : `/panel/kesfet/profil/${post.authorUserId}`}
                        className={cn("text-sm font-semibold hover:underline", isRose ? "text-[#5a3f60]" : "text-white")}
                      >
                        {post.authorName}
                      </Link>
                      <div className={cn("text-xs", isRose ? "text-[#7f698b]" : "text-white/85")}>@{post.authorHandle} • {formatRelative(post.createdAt)}</div>
                    </div>
                  </div>

                  {isEditing ? (
                    <div className="px-4 pb-2">
                      <Textarea
                        value={editingBody}
                        onChange={(event) => setEditingBody(event.target.value)}
                        maxLength={3000}
                        className="min-h-[100px] resize-none rounded-xl border-white/30 bg-white/70 text-[#3d2f47]"
                      />
                      <div className="mt-2 flex justify-end gap-2">
                        <Button type="button" size="sm" variant="secondary" onClick={() => setEditingPostId(null)}>
                          Vazgeç
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          disabled={editPostMutation.isPending || (!editingBody.trim() && !post.media.length)}
                          onClick={() => void editPostMutation.mutate({ postId: post.id, body: editingBody })}
                        >
                          Kaydet
                        </Button>
                      </div>
                    </div>
                  ) : null}

                  {renderPostMedia(post.media)}

                  <div className={cn("space-y-3 border-t px-4 pb-4 pt-3", isRose ? "border-[#f0d8e6] bg-white/45" : "border-white/20 bg-[#7f66dd]/55")}>
                    <div className="flex items-center gap-2 text-xs">
                      <Eye className={cn("h-3.5 w-3.5", isRose ? "text-[#815a72]" : "text-white/90")} />
                      <span className={cn(isRose ? "text-[#6f4f63]" : "text-white/90")}>{post.viewCount} kişi gördü</span>
                      <span className={cn("ml-2 rounded-full px-2 py-0.5 font-semibold", isRose ? "bg-[#f7dde8] text-[#8f4f73]" : "bg-white/20 text-white")}>Kolektif Enerji %{post.collectiveEnergyPercent}</span>
                    </div>

                    <div className={cn("h-2 overflow-hidden rounded-full", isRose ? "bg-[#efdae5]" : "bg-white/20")}>
                      <div
                        className={cn("h-full rounded-full", isRose ? "bg-[#d676a7]" : "bg-white")}
                        style={{ width: `${Math.max(2, post.collectiveEnergyPercent)}%` }}
                      />
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      <div className={cn("text-xs", isRose ? "text-[#6f4f63]" : "text-white/90")}>
                        {post.likeCount} enerji • {post.saveCount} şifa
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className={ENERGY_BUTTON_CLASS}
                          onClick={() => void handlePostInteractionClick(post.id, "energy")}
                        >
                          <Zap className={cn("h-3.5 w-3.5", post.likedByMe && "fill-current")} />
                        </Button>

                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className={HEALING_BUTTON_CLASS}
                          onClick={() => void handlePostInteractionClick(post.id, "healing")}
                        >
                          <Heart className={cn("h-3.5 w-3.5", post.savedByMe && "fill-current")} />
                        </Button>
                      </div>
                    </div>

                    {renderPostComments(post, isRose ? "default" : "dark")}
                  </div>
                </Card>
              );
            }

            if (practiceCompletion) {
              const sourceCards = practiceCompletion.kind === "nefes" ? (breathsQuery.data ?? []) : (meditationsQuery.data ?? []);
              const completionVisual = resolveCompletionVisuals({
                title: practiceCompletion.title,
                primaryCards: sourceCards,
              });
              const completedLabel = practiceCompletion.kind === "nefes" ? "nefes çalışmasını" : "meditasyonunu";
              const ctaHref = practiceCompletion.kind === "nefes" ? "/panel/nefes" : "/panel/meditasyon";
              const streakDays = resolveCompletionStreakDays({
                activities: activitiesQuery.data ?? [],
                authorUserId: post.authorUserId,
                title: practiceCompletion.title,
                kind: practiceCompletion.kind,
                createdAt: post.createdAt,
              });

              return (
                <Card key={item.id} className={PREMIUM_FEED_CARD_CLASS}>
                  <div className="relative px-3 pb-4 pt-4 sm:px-6 sm:pb-5 sm:pt-6">
                    {completionVisual.coverImageUrl ? (
                      <>
                        <div className="pointer-events-none absolute inset-0">
                          <img src={completionVisual.coverImageUrl} alt={practiceCompletion.title} className="h-full w-full object-cover opacity-50" onError={(event) => applyCoverFallbackImage(event.currentTarget)} />
                        </div>
                        <div className={PREMIUM_OVERLAY_GRADIENT_CLASS} />
                      </>
                    ) : (
                      <div className={PREMIUM_OVERLAY_GRADIENT_CLASS} />
                    )}

                    {isOwnPost ? (
                      <div className="relative z-10 mb-2 flex justify-end">
                        <div className="relative">
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            className="h-8 w-8 rounded-full border-white/90 bg-white text-slate-700 shadow-sm hover:bg-slate-100"
                            onClick={() => setMenuPostId((prev) => (prev === post.id ? null : post.id))}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>

                          {menuPostId === post.id ? (
                            <div className="absolute right-0 z-20 mt-2 w-36 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg">
                              <button
                                type="button"
                                className="flex w-full items-center rounded-lg px-2 py-1.5 text-sm hover:bg-slate-100"
                                onClick={() => {
                                  setEditingPostId(post.id);
                                  setEditingBody(post.body);
                                  setMenuPostId(null);
                                }}
                              >
                                <Pencil className="mr-2 h-4 w-4" /> Düzenle
                              </button>
                              <button
                                type="button"
                                disabled={deletePostMutation.isPending}
                                className="flex w-full items-center rounded-lg px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-60"
                                onClick={() => {
                                  void deletePostMutation.mutate(post.id);
                                  setMenuPostId(null);
                                }}
                              >
                                <Trash2 className="mr-2 h-4 w-4" /> Sil
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ) : null}

                    <div className="relative mx-auto max-w-2xl text-center">
                      <h3 className="break-words text-xl font-black leading-tight tracking-tight text-[#0f172a] sm:text-3xl">
                        {post.authorName}, {practiceCompletion.title} {completedLabel} tamamladı!
                      </h3>
                      <p className="mt-1 text-sm text-slate-600 sm:text-lg">Toplulukla paylaşıldı</p>

                      <div className="mt-4 grid grid-cols-3 gap-2 sm:mt-5 sm:gap-3">
                        <div className={PREMIUM_METRIC_CARD_CLASS}>
                          <div className="break-words text-[10px] font-bold uppercase leading-tight tracking-[0.08em] text-slate-500">Süre</div>
                          <div className="mt-1 text-2xl font-black leading-none text-[#d96a23] sm:text-3xl">{formatSessionDuration(completionVisual.durationMinutes)}</div>
                        </div>
                        <div className={PREMIUM_METRIC_CARD_CLASS}>
                          <div className="break-words text-[10px] font-bold uppercase leading-tight tracking-[0.08em] text-slate-500">Kolektif Enerji</div>
                          <div className="mt-1 text-2xl font-black leading-none text-[#d96a23] sm:text-3xl">{formatFocusPercent(post.collectiveEnergyPercent)}</div>
                        </div>
                        <div className={PREMIUM_METRIC_CARD_CLASS}>
                          <div className="break-words text-[10px] font-bold uppercase leading-tight tracking-[0.08em] text-slate-500">Seri</div>
                          <div className="mt-1 text-2xl font-black leading-none text-[#d96a23] sm:text-3xl">{streakDays} G.</div>
                        </div>
                      </div>

                      <div className="mt-5 flex justify-center">
                        <Button asChild type="button" className="rounded-xl bg-white text-slate-900 hover:bg-white/90">
                          <Link to={ctaHref}>Sen de Tamamla</Link>
                        </Button>
                      </div>

                      <div className="mt-4 flex flex-col items-center justify-center gap-2">
                        <Link to={post.authorUserId === user?.id ? "/panel/profil" : `/panel/kesfet/profil/${post.authorUserId}`} className="rounded-full">
                          <Avatar className="h-12 w-12 border border-white/70 shadow-sm">
                            <AvatarImage src={post.authorPhotoUrl ?? ""} alt={post.authorName} />
                            <AvatarFallback>{initials(post.authorName)}</AvatarFallback>
                          </Avatar>
                        </Link>
                        <Link to={post.authorUserId === user?.id ? "/panel/profil" : `/panel/kesfet/profil/${post.authorUserId}`} className="text-base font-semibold text-slate-900 hover:underline">
                          {post.authorName}
                        </Link>
                        <div className="break-words px-2 text-center text-[11px] text-slate-500 sm:text-xs">@{post.authorHandle} • {formatRelative(post.createdAt)}</div>
                      </div>
                    </div>
                  </div>

                  <div className={PREMIUM_FOOTER_CLASS}>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Eye className="h-3.5 w-3.5" />
                      <span>{post.viewCount} görüntülenme</span>

                    </div>

                    <div className={PREMIUM_ENERGY_BAR_CLASS}>
                      <div className={PREMIUM_ENERGY_BAR_FILL_CLASS} style={{ width: `${Math.max(2, post.collectiveEnergyPercent)}%` }} />
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs text-slate-500">{post.likeCount} enerji • {post.saveCount} şifa</div>

                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className={ENERGY_BUTTON_CLASS}
                          onClick={() => void handlePostInteractionClick(post.id, "energy")}
                        >
                          <Zap className={cn("h-3.5 w-3.5", post.likedByMe && "fill-current")} />
                        </Button>

                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className={HEALING_BUTTON_CLASS}
                          onClick={() => void handlePostInteractionClick(post.id, "healing")}
                        >
                          <Heart className={cn("h-3.5 w-3.5", post.savedByMe && "fill-current")} />
                        </Button>
                      </div>
                    </div>

                    {renderPostComments(post)}
                  </div>
                </Card>
              );
            }

            if (activityCompletion) {
              const sourceCards = wellnessActivitiesQuery.data ?? [];
              const fallbackCards = [...(meditationsQuery.data ?? []), ...(breathsQuery.data ?? []), ...(dietsQuery.data ?? [])];
              const completionVisual = resolveCompletionVisuals({
                title: activityCompletion.title,
                primaryCards: sourceCards,
                fallbackCards,
              });
              const ctaHref = activityCompletion.kind === "rituel" ? "/panel/rituel" : "/panel/aktivite";
              const completedLabel = activityCompletion.kind === "rituel" ? "ritüelini" : "aktivitesini";
              const streakDays = resolveCompletionStreakDays({
                activities: activitiesQuery.data ?? [],
                authorUserId: post.authorUserId,
                title: activityCompletion.title,
                kind: activityCompletion.kind,
                createdAt: post.createdAt,
              });

              return (
                <Card key={item.id} className={PREMIUM_FEED_CARD_CLASS}>
                  <div className="relative px-3 pb-4 pt-4 sm:px-6 sm:pb-5 sm:pt-6">
                    {completionVisual.coverImageUrl ? (
                      <>
                        <div className="pointer-events-none absolute inset-0">
                          <img src={completionVisual.coverImageUrl} alt={activityCompletion.title} className="h-full w-full object-cover opacity-50" onError={(event) => applyCoverFallbackImage(event.currentTarget)} />
                        </div>
                        <div className={PREMIUM_OVERLAY_GRADIENT_CLASS} />
                      </>
                    ) : (
                      <div className={PREMIUM_OVERLAY_GRADIENT_CLASS} />
                    )}

                    {isOwnPost ? (
                      <div className="relative z-10 mb-2 flex justify-end">
                        <div className="relative">
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            className="h-8 w-8 rounded-full border-white/90 bg-white text-slate-700 shadow-sm hover:bg-slate-100"
                            onClick={() => setMenuPostId((prev) => (prev === post.id ? null : post.id))}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>

                          {menuPostId === post.id ? (
                            <div className="absolute right-0 z-20 mt-2 w-36 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg">
                              <button
                                type="button"
                                className="flex w-full items-center rounded-lg px-2 py-1.5 text-sm hover:bg-slate-100"
                                onClick={() => {
                                  setEditingPostId(post.id);
                                  setEditingBody(post.body);
                                  setMenuPostId(null);
                                }}
                              >
                                <Pencil className="mr-2 h-4 w-4" /> Düzenle
                              </button>
                              <button
                                type="button"
                                disabled={deletePostMutation.isPending}
                                className="flex w-full items-center rounded-lg px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-60"
                                onClick={() => {
                                  void deletePostMutation.mutate(post.id);
                                  setMenuPostId(null);
                                }}
                              >
                                <Trash2 className="mr-2 h-4 w-4" /> Sil
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ) : null}

                    <div className="relative mx-auto max-w-2xl text-center">
                      <h3 className="break-words text-xl font-black leading-tight tracking-tight text-[#0f172a] sm:text-3xl">
                        {post.authorName}, {activityCompletion.title} {completedLabel} tamamladı!
                      </h3>
                      <p className="mt-1 text-sm text-slate-600 sm:text-lg">Toplulukla paylaşıldı</p>

                      <div className="mt-4 grid grid-cols-3 gap-2 sm:mt-5 sm:gap-3">
                        <div className={PREMIUM_METRIC_CARD_CLASS}>
                          <div className="break-words text-[10px] font-bold uppercase leading-tight tracking-[0.08em] text-slate-500">Süre</div>
                          <div className="mt-1 text-2xl font-black leading-none text-[#d96a23] sm:text-3xl">{formatSessionDuration(completionVisual.durationMinutes)}</div>
                        </div>
                        <div className={PREMIUM_METRIC_CARD_CLASS}>
                          <div className="break-words text-[10px] font-bold uppercase leading-tight tracking-[0.08em] text-slate-500">Kolektif Enerji</div>
                          <div className="mt-1 text-2xl font-black leading-none text-[#d96a23] sm:text-3xl">{formatFocusPercent(post.collectiveEnergyPercent)}</div>
                        </div>
                        <div className={PREMIUM_METRIC_CARD_CLASS}>
                          <div className="break-words text-[10px] font-bold uppercase leading-tight tracking-[0.08em] text-slate-500">Seri</div>
                          <div className="mt-1 text-2xl font-black leading-none text-[#d96a23] sm:text-3xl">{streakDays} G.</div>
                        </div>
                      </div>

                      <div className="mt-5 flex justify-center">
                        <Button asChild type="button" className="rounded-xl bg-white text-slate-900 hover:bg-white/90">
                          <Link to={ctaHref}>Sen de Tamamla</Link>
                        </Button>
                      </div>

                      <div className="mt-4 flex flex-col items-center justify-center gap-2">
                        <Link to={post.authorUserId === user?.id ? "/panel/profil" : `/panel/kesfet/profil/${post.authorUserId}`} className="rounded-full">
                          <Avatar className="h-12 w-12 border border-white/70 shadow-sm">
                            <AvatarImage src={post.authorPhotoUrl ?? ""} alt={post.authorName} />
                            <AvatarFallback>{initials(post.authorName)}</AvatarFallback>
                          </Avatar>
                        </Link>
                        <Link to={post.authorUserId === user?.id ? "/panel/profil" : `/panel/kesfet/profil/${post.authorUserId}`} className="text-base font-semibold text-slate-900 hover:underline">
                          {post.authorName}
                        </Link>
                        <div className="break-words px-2 text-center text-[11px] text-slate-500 sm:text-xs">@{post.authorHandle} • {formatRelative(post.createdAt)}</div>
                      </div>
                    </div>
                  </div>

                  <div className={PREMIUM_FOOTER_CLASS}>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Eye className="h-3.5 w-3.5" />
                      <span>{post.viewCount} görüntülenme</span>

                    </div>

                    <div className={PREMIUM_ENERGY_BAR_CLASS}>
                      <div className={PREMIUM_ENERGY_BAR_FILL_CLASS} style={{ width: `${Math.max(2, post.collectiveEnergyPercent)}%` }} />
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs text-slate-500">{post.likeCount} enerji • {post.saveCount} şifa</div>

                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className={ENERGY_BUTTON_CLASS}
                          onClick={() => void handlePostInteractionClick(post.id, "energy")}
                        >
                          <Zap className={cn("h-3.5 w-3.5", post.likedByMe && "fill-current")} />
                        </Button>

                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className={HEALING_BUTTON_CLASS}
                          onClick={() => void handlePostInteractionClick(post.id, "healing")}
                        >
                          <Heart className={cn("h-3.5 w-3.5", post.savedByMe && "fill-current")} />
                        </Button>
                      </div>
                    </div>

                    {renderPostComments(post)}
                  </div>
                </Card>
              );
            }

            return (
              <Card
                key={item.id}
                onDoubleClick={(event) => void handlePostDoubleClick(post, event.target)}
                className="overflow-hidden rounded-3xl border border-[#e7d8e9] bg-[#f8f3f7] shadow-sm"
              >
                <div className="p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <Avatar className="h-10 w-10 border border-slate-200">
                        <AvatarImage src={post.authorPhotoUrl ?? ""} alt={post.authorName} />
                        <AvatarFallback>{initials(post.authorName)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{post.authorName}</div>
                        <div className="truncate text-xs text-slate-500">@{post.authorHandle} • {formatRelative(post.createdAt)}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {post.tags.length ? <div className="text-[11px] font-semibold uppercase tracking-wide text-[#8f5b97]">#{post.tags[0]}</div> : null}
                      {isOwnPost ? (
                        <div className="relative">
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            className="h-7 w-7 rounded-lg p-0"
                            onClick={() => setMenuPostId((prev) => (prev === post.id ? null : post.id))}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>

                          {menuPostId === post.id ? (
                            <div className="absolute right-0 z-20 mt-2 w-36 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg">
                              <button
                                type="button"
                                className="flex w-full items-center rounded-lg px-2 py-1.5 text-sm hover:bg-slate-100"
                                onClick={() => {
                                  setEditingPostId(post.id);
                                  setEditingBody(post.body);
                                  setMenuPostId(null);
                                }}
                              >
                                <Pencil className="mr-2 h-4 w-4" /> Düzenle
                              </button>
                              <button
                                type="button"
                                disabled={deletePostMutation.isPending}
                                className="flex w-full items-center rounded-lg px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-60"
                                onClick={() => {
                                  void deletePostMutation.mutate(post.id);
                                  setMenuPostId(null);
                                }}
                              >
                                <Trash2 className="mr-2 h-4 w-4" /> Sil
                              </button>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {isEditing ? (
                    <div className="mt-3 space-y-2">
                      <Textarea
                        value={editingBody}
                        onChange={(event) => setEditingBody(event.target.value)}
                        maxLength={3000}
                        className="min-h-[100px] resize-none rounded-xl border-[#ddc6df] bg-[#f9f3fa] text-[#3d2f47]"
                      />
                      <div className="flex justify-end gap-2">
                        <Button type="button" size="sm" variant="outline" onClick={() => setEditingPostId(null)}>
                          Vazgeç
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          disabled={editPostMutation.isPending || (!editingBody.trim() && !post.media.length)}
                          onClick={() => void editPostMutation.mutate({ postId: post.id, body: editingBody })}
                        >
                          Kaydet
                        </Button>
                      </div>
                    </div>
                  ) : post.body ? (
                    <div className="mt-3 rounded-2xl border border-white/70 bg-white/65 p-4">
                      {renderExpandablePostBody({
                        postId: post.id,
                        body: post.body,
                        textClassName: "text-base leading-relaxed text-slate-700",
                        buttonClassName: "text-[#7a4a95]",
                      })}
                    </div>
                  ) : null}
                </div>

                {renderPostMedia(post.media)}

                <div className="space-y-3 border-t border-[#e7dce9] bg-white/80 p-3">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Eye className="h-3.5 w-3.5" />
                    <span>{post.viewCount} görüntülenme</span>
                    <span className={PREMIUM_ENERGY_BADGE_CLASS}>Kolektif Enerji %{post.collectiveEnergyPercent}</span>
                  </div>

                  <div className={PREMIUM_ENERGY_BAR_CLASS}>
                    <div className={PREMIUM_ENERGY_BAR_FILL_CLASS} style={{ width: `${Math.max(2, post.collectiveEnergyPercent)}%` }} />
                  </div>

                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-xs text-slate-500">{post.likeCount} enerji • {post.saveCount} şifa</div>

                    <div className="flex items-center gap-2 self-end">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className={ENERGY_BUTTON_CLASS}
                        onClick={() => void handlePostInteractionClick(post.id, "energy")}
                      >
                        <Zap className={cn("h-3.5 w-3.5", post.likedByMe && "fill-current")} />
                      </Button>

                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className={HEALING_BUTTON_CLASS}
                        onClick={() => void handlePostInteractionClick(post.id, "healing")}
                      >
                        <Heart className={cn("h-3.5 w-3.5", post.savedByMe && "fill-current")} />
                      </Button>
                    </div>
                  </div>

                  {renderPostComments(post)}
                </div>
              </Card>
            );
          })
        ) : (
          <Card className="rounded-2xl border border-dashed border-primary/30 p-6 text-sm text-slate-600">Henüz akış içeriği yok.</Card>
        )}
      </div>
    </div>
  );
}
